import React, { useEffect, useMemo, useCallback } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Filter } from 'lucide-react';
import { Layout } from '../components/Layout/Layout';
import { ProjectCard } from '../components/Projects/ProjectCard';
import { ProjectAssignmentModal } from '../components/Projects/ProjectAssignmentModal';
import { useProjectStore } from '../store/projectStore';
import { useAuthStore } from '../store/authStore';
import { ProfileType, Project } from '../types';

export const ProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const projects = useProjectStore(state => state.projects);
  const fetchProjects = useProjectStore(state => state.fetchProjects);
  const isLoading = useProjectStore(state => state.isLoading);
  const user = useAuthStore(state => state.user);
  const [selectedProjectForAssignment, setSelectedProjectForAssignment] = useState<Project | null>(null);
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const canCreateProject = useMemo(() =>
    user?.profile_type === ProfileType.PORTEUR,
    [user?.profile_type]
  );

  const handleCreateProject = useCallback(() => {
    navigate('/projects/new');
  }, [navigate]);

  const handleAssignAdvisor = useCallback((project: Project) => {
    setSelectedProjectForAssignment(project);
    setIsAssignmentModalOpen(true);
  }, []);

  const handleAssignmentSuccess = useCallback(() => {
    setIsAssignmentModalOpen(false);
    setSelectedProjectForAssignment(null);
    fetchProjects();
  }, [fetchProjects]);

  const canAssignAdvisor = useMemo(() =>
    user && [ProfileType.COORDINATEUR, ProfileType.ADMIN].includes(user.profile_type as ProfileType),
    [user]
  );
  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Mes projets</h1>
            <p className="text-gray-600 mt-1">
              Gérez et suivez l'avancement de vos projets ESS
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            <button className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <Filter className="w-4 h-4" />
              <span>Filtrer</span>
            </button>
            
            {canCreateProject && (
              <button
                onClick={handleCreateProject}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Nouveau projet</span>
              </button>
            )}
          </div>
        </div>

        {/* Projects Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : projects.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <ProjectCard 
                key={project.id} 
                project={project} 
                onAssignAdvisor={canAssignAdvisor ? handleAssignAdvisor : undefined}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Plus className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun projet</h3>
            <p className="text-gray-600 mb-6">
              {canCreateProject 
                ? "Vous n'avez pas encore créé de projet. Commencez dès maintenant !"
                : "Aucun projet n'est actuellement assigné à votre compte."
              }
            </p>
            {canCreateProject && (
              <button
                onClick={handleCreateProject}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Créer mon premier projet
              </button>
            )}
          </div>
        )}

        {/* Assignment Modal */}
        {selectedProjectForAssignment && (
          <ProjectAssignmentModal
            isOpen={isAssignmentModalOpen}
            onClose={() => {
              setIsAssignmentModalOpen(false);
              setSelectedProjectForAssignment(null);
            }}
            project={selectedProjectForAssignment}
            onSuccess={handleAssignmentSuccess}
          />
        )}
      </div>
    </Layout>
  );
};